import React, { ChangeEvent, useCallback } from 'react';
import { TFunction } from 'i18next';
import LiveRegion from '../../../components/atoms/LiveRegion/LiveRegion';
import { FormUtils, useFormContext, InputType } from '../../../components/molecules/Form';
import { Namespace } from '../../../translation/namespace';
import { InteractionStatus } from '../../../types/InteractionStatus';
import FormSelect, { optionsProcessor } from '../../../components/molecules/Form/components/FormSelect/FormSelect';
import FormsRow from '../../../components/molecules/Form/components/FormsRow/FormsRow';
import { translateWithCustomTransMarker } from '../../../lib/translation/utils';
import FormsComboBoxMultiSelect, {
  BREED,
} from '../../../components/molecules/Form/components/FormsComboboxMultiSelect/FormsComboBoxMultiSelect';
import { murmurhash } from '../../../lib/js/hash';
import FormDateTime, {
  FormDateTimeModifier,
} from '../../../components/molecules/Form/components/FormDateTime/FormDateTime';
import { ContentParagraph } from '../../../_lib/components/ContentParagraph';
import { useSonTranslation } from '../../../translation/useSonTranslation';
// eslint-disable-next-line max-len
import FormsElementContainer from '../../../components/molecules/Form/components/FormsElementContainer/FormsElementContainer';
import { setPartOfStringBold } from '../../../lib/StringUtils/StringHTMLUtils';
import { FormSubmitButton } from '../../../components/molecules/Form/components/FormSubmitButton/FormSubmitButton';

export function PagePropertySettingsFormTemplate() {
  const { inputs, onChange, submitActionEnabled, getStatus, model } = useFormContext();
  const { t, i18n } = useSonTranslation('PagePropertySettingsForm');

  const uniqueId = useCallback(
    (addon: string) => {
      return `${addon}-${murmurhash(model.url)}`;
    },
    [model.url],
  );

  const statusMessages = () => {
    return {
      [InteractionStatus.SUCCESS]: FormUtils.createStatusMessage(t('status.success')),
    };
  };

  return (
    <>
      {inputs.teamroomAdmissionType && (
        <FormsRow>
          <FormSelect
            id={`select-${uniqueId(inputs.teamroomAdmissionType.name)}`}
            name={inputs.teamroomAdmissionType.name}
            required={inputs.teamroomAdmissionType.required}
            labelTitle={t('inputs.teamroomAdmissionType.label')}
            value={inputs.teamroomAdmissionType.value}
            options={optionsProcessor(
              inputs.teamroomAdmissionType.name,
              inputs.teamroomAdmissionType.options as any,
              Namespace.PagePropertySettingsForm,
            )}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.name, event.target.value)}
          />
        </FormsRow>
      )}
      {inputs.readRestrictionInstruction && (
        <FormsRow>
          <FormsElementContainer>
            <h3 className="page-property-settings-form-template_heading">{t('inputs.readRestriction.label')}</h3>
            <ContentParagraph>
              {setPartOfStringBold(
                `PagePropertySettingsForm:inputs.${inputs.readRestrictionInstruction.name}.` +
                `${inputs.readRestrictionInstruction.value}`,
                i18n,
                t as TFunction,
              )}
            </ContentParagraph>
          </FormsElementContainer>
        </FormsRow>
      )}
      {inputs.readRestriction && (
        <FormsRow>
          <FormSelect
            id={`select-${uniqueId(inputs.readRestriction.name)}`}
            name={inputs.readRestriction.name}
            required={inputs.readRestriction.required}
            labelTitle={t('inputs.readRestriction.label')}
            value={inputs.readRestriction.value}
            options={optionsProcessor(
              inputs.readRestriction.name,
              inputs.readRestriction.options as any,
              Namespace.PagePropertySettingsForm,
            )}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.name, event.target.value)}
          />
        </FormsRow>
      )}
      {inputs.confidentialLevel && (
        <FormsRow>
          <FormSelect
            id={`select-${uniqueId(inputs.confidentialLevel.name)}`}
            name={inputs.confidentialLevel.name}
            required={inputs.confidentialLevel.required}
            labelTitle={translateWithCustomTransMarker(t('inputs.confidentialLevel.label')) as any}
            value={inputs.confidentialLevel.value}
            options={optionsProcessor(
              inputs.confidentialLevel.name,
              inputs.confidentialLevel.options as any,
              Namespace.PagePropertySettingsForm,
            )}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => onChange(event.target.name, event.target.value)}
          />
        </FormsRow>
      )}
      {inputs.author?.type === InputType.COMBOBOXMULTISELECT && (
        <FormsRow>
          <FormsComboBoxMultiSelect
            breed={BREED.PersonCollection}
            label={t('inputs.author.label')}
            maxSelectableItems={1}
            // @ts-expect-error -- our form typing sucks...scusi
            parameter={inputs.author}
          />
        </FormsRow>
      )}
      {inputs.editorialResponsible?.type === InputType.COMBOBOXMULTISELECT && (
        <FormsRow>
          <FormsComboBoxMultiSelect
            breed={BREED.PersonCollection}
            label={t('inputs.editorialResponsible.label')}
            maxSelectableItems={1}
            // @ts-expect-error - see above
            parameter={inputs.editorialResponsible}
          />
        </FormsRow>
      )}
      {inputs.contentDate && (
        <FormsRow>
          <FormDateTime
            modifier={[FormDateTimeModifier.FULL_WIDTH]}
            label={t('inputs.contentDate.label')}
            labelHelpText={t('inputs.contentDate.labelHelpText')}
            name={inputs.contentDate.name}
            value={inputs.contentDate?.value}
            required={inputs.contentDate.required}
            onChange={({ target }) => onChange(target.name, target.value)}
          />
        </FormsRow>
      )}
      <FormSubmitButton buttonLabel={t('saveChanges')} enabled={submitActionEnabled()} />
      <LiveRegion htmlTag="div" invisible={false} ariaAtomic ariaLive="polite">
        {getStatus(statusMessages())}
      </LiveRegion>
    </>
  );
}
